import { useMemo } from 'react';
import { useListCustomersAutocomplete } from '@/jobs/application/adapters/listCustomersAutocomplete';
import { useGetCustomerCallerId } from '@/customers/application/adapters/getCustomerCallerId';
import { useContactOptions, ContactOption } from '@/common/presentation/hooks/useContactOptions';
import { ALLOWED_CALLER_ID_TYPES_SET } from '@/customers/application/types/CustomerTypes';
import { OptionType } from '@/common/presentation/components/forms/fields/FormikAutocomplete';

type CustomerOption = { id?: number | null; companyName?: string | null };

interface UseConfirmedByContactsParams {
  customerId?: number | null;
  confirmedBy?: string | number | null;
}

export const useConfirmedByContacts = ({ customerId, confirmedBy }: UseConfirmedByContactsParams) => {
  // Get customer data
  const { rawCustomerData } = useListCustomersAutocomplete();

  const getSelectedCustomerData = (): CustomerOption | undefined => {
    if (!customerId || !rawCustomerData?.length) return undefined;
    return rawCustomerData.find((customer: CustomerOption) => customer.id === customerId);
  };

  // Get customer contacts
  const { loading: customerCallerLoading, contacts: customerContacts } = useGetCustomerCallerId(
    customerId ?? undefined,
    getSelectedCustomerData()
  );

  const { options: contactOptions } = useContactOptions({
    contacts: customerContacts,
    rawCaller: undefined,
    allowedTypes: ALLOWED_CALLER_ID_TYPES_SET
  });

  // Build contact options for "Confirmed By" autocomplete
  const confirmedByContactOptions = useMemo((): OptionType[] => {
    return (
      contactOptions.reduce((acc: ContactOption[], opt: ContactOption) => {
        if (!acc.some((o) => o.name === opt.name)) {
          acc.push(opt);
        }
        return acc;
      }, []) ?? []
    ).map((opt) => ({ value: opt.name, label: opt.name }));
  }, [contactOptions]);

  // Get initial option for "Confirmed By" field
  const confirmedByInitialOption = useMemo<OptionType | undefined>(() => {
    if (!confirmedBy) return undefined;
    return confirmedByContactOptions.find((o) => String(o.value) === String(confirmedBy));
  }, [confirmedBy, confirmedByContactOptions]);

  return {
    confirmedByContactOptions,
    confirmedByInitialOption,
    customerCallerLoading
  };
};
